import {
  type BoltCount,
  CARRY_CONTACT_EPSILON,
  type DuoInputs,
  type EntitySnapshot,
  GRAVITY_TILES_PER_S2,
  JUMP_SPEED_TILES_PER_S,
  MAGNET_ENERGY_MAX,
  MAX_STARS,
  MOVE_SPEED_TILES_PER_S,
  moveAxis,
  neutralDuoInputs,
  PLAYER_HEIGHT_TILES,
  PLAYER_INDICES,
  PLAYER_WIDTH_TILES,
  type PlayerIndex,
  type PlayerSnapshot,
  RESPAWN_DELAY_TICKS,
  type SimStateSnapshot,
  type SimStatus,
  TICK_DT,
  TICK_RATE,
} from '@md/shared';
import {
  cloneEntity,
  type EntityCtx,
  type EntityInstance,
  entityAabb,
  entitySnapshot,
} from '../entities/entity.js';
import type { ParsedLevel } from '../level/parser.js';
import type { AbilityCtx, GameModule, InteractionCtx } from '../module/game-module.js';
import type { CharacterDef, PlayerRuntime } from '../module/player.js';
import {
  aabbCenter,
  aabbContainsPoint,
  aabbOverlaps,
  aabbTouches,
  tileAabb,
} from '../physics/aabb.js';
import { type Body, bodyAabb, cloneBody, createBody } from '../physics/body.js';
import { integrateBody } from '../physics/integrator.js';
import { findSupport, moveBody, type Obstacle } from '../physics/world.js';
import { createRng, type RngState } from './rng.js';

/**
 * État d'un robot dans la simulation.
 *
 * Structurellement un `PlayerRuntime` : c'est cet objet même qui est confié
 * aux abilities et aux règles d'interaction du module, sans copie ni adaptateur.
 */
export interface SimPlayer extends PlayerRuntime {
  readonly character: CharacterDef;
  /** Mémorise l'entrée `jump` du tick précédent (saut sur front montant). */
  jumpHeld: boolean;
}

/**
 * État complet d'une partie. Immutable vu de l'extérieur : `tick()` renvoie
 * toujours un nouvel objet (spec §3).
 */
export interface SimState {
  readonly level: ParsedLevel;
  readonly gameId: string;
  readonly seed: number;
  readonly tick: number;
  readonly rng: RngState;
  readonly players: readonly [SimPlayer, SimPlayer];
  readonly entities: readonly EntityInstance[];
  readonly inputs: DuoInputs;
  readonly status: SimStatus;
  readonly deaths: number;
  /** Ticks restants avant réinitialisation après une mort (0 = aucune). */
  readonly respawnTicks: number;
  readonly wonTick: number | null;
  /** Étoiles obtenues (0 tant que le niveau n'est pas terminé). */
  readonly stars: number;
}

function clonePlayer(player: SimPlayer): SimPlayer {
  return {
    index: player.index,
    character: player.character,
    body: cloneBody(player.body),
    facing: player.facing,
    dead: player.dead,
    atExit: player.atExit,
    jumpHeld: player.jumpHeld,
    energy: player.energy,
    fieldActive: player.fieldActive,
    gravityScale: player.gravityScale,
    flags: { ...player.flags },
  };
}

function spawnPlayer(level: ParsedLevel, module: GameModule, index: PlayerIndex): SimPlayer {
  const spawn = level.spawns[index];
  return {
    index,
    character: module.characters[index],
    body: createBody(spawn.x, spawn.y, PLAYER_WIDTH_TILES, PLAYER_HEIGHT_TILES),
    facing: index === 0 ? 1 : -1,
    dead: false,
    atExit: false,
    jumpHeld: false,
    energy: MAGNET_ENERGY_MAX,
    fieldActive: false,
    gravityScale: 1,
    flags: {},
  };
}

/** Instancie les entités du niveau via les factories du module. */
export function createEntities(level: ParsedLevel, module: GameModule): EntityInstance[] {
  const entities: EntityInstance[] = [];
  for (const def of level.entities) {
    const factory = module.entityFactories[def.type];
    if (factory) {
      entities.push(factory(def));
    }
  }
  return entities;
}

/** Crée l'état initial d'une partie. */
export function createSimState(level: ParsedLevel, module: GameModule, seed = 0): SimState {
  return {
    level,
    gameId: module.id,
    seed,
    tick: 0,
    rng: createRng(seed),
    players: [spawnPlayer(level, module, 0), spawnPlayer(level, module, 1)],
    entities: createEntities(level, module),
    inputs: neutralDuoInputs(),
    status: 'playing',
    deaths: 0,
    respawnTicks: 0,
    wonTick: null,
    stars: 0,
  };
}

/** Remet les robots au spawn sans toucher au compteur de ticks. */
function respawnedPlayers(level: ParsedLevel, module: GameModule): readonly [SimPlayer, SimPlayer] {
  return [spawnPlayer(level, module, 0), spawnPlayer(level, module, 1)];
}

/**
 * La hitbox touche-t-elle une tuile mortelle (pics, ou tuile létale pour CE
 * personnage) ?
 *
 * Une tuile SOLIDE (dalle électrifiée) tue au contact : un robot ne peut jamais
 * la recouvrir, il s'arrête sur sa face. Une tuile traversable (pics, `@`)
 * exige un recouvrement réel, sinon on mourrait en passant à côté.
 */
function touchesLethalTile(
  body: Body,
  level: ParsedLevel,
  characterLethalTiles: readonly string[],
): boolean {
  const box = bodyAabb(body);
  const tx0 = Math.floor(body.x) - 1;
  const tx1 = Math.floor(body.x + body.w) + 1;
  const ty0 = Math.floor(body.y) - 1;
  const ty1 = Math.floor(body.y + body.h) + 1;
  for (let ty = ty0; ty <= ty1; ty += 1) {
    for (let tx = tx0; tx <= tx1; tx += 1) {
      const tile = level.tileAt(tx, ty);
      const lethal = tile.lethal || characterLethalTiles.includes(tile.char);
      if (!lethal) {
        continue;
      }
      const cell = tileAabb(tx, ty);
      if (tile.solid ? aabbTouches(box, cell) : aabbOverlaps(box, cell)) {
        return true;
      }
    }
  }
  return false;
}

/** Boîtes solides du tick : portes fermées, blocs, ascenseurs. */
function solidObstacles(entities: readonly EntityInstance[]): Obstacle[] {
  const obstacles: Obstacle[] = [];
  for (const entity of entities) {
    if (entity.solid) {
      obstacles.push({ id: entity.id, box: entityAabb(entity), oneWay: entity.oneWay });
    }
  }
  return obstacles;
}

/** Décompte des collectibles du niveau (2ᵉ étoile, concept §4.2). */
export function countBolts(entities: readonly EntityInstance[]): BoltCount {
  let total = 0;
  let collected = 0;
  for (const entity of entities) {
    if (entity.flags.collectible === 1) {
      total += 1;
      if (entity.flags.collected === 1) {
        collected += 1;
      }
    }
  }
  return { collected, total };
}

/**
 * Étoiles de fin de niveau (concept §4.2) : terminé → 1, tous les boulons → 2,
 * et sous le temps par → 3.
 */
export function computeStars(state: SimState): number {
  const bolts = countBolts(state.entities);
  const allBolts = bolts.collected >= bolts.total;
  const seconds = state.tick / TICK_RATE;
  const underPar = seconds <= state.level.meta.parTimeSeconds;
  return Math.min(MAX_STARS, 1 + (allBolts ? 1 : 0) + (allBolts && underPar ? 1 : 0));
}

/**
 * Un tick de simulation — fonction PURE à pas fixe (spec §3).
 *
 * Ordre d'update figé :
 * `inputs → abilities → interactions → intégration → collisions AABB →
 *  entités → transport → dangers/mort → conditions de victoire`.
 */
export function tick(state: SimState, inputs: DuoInputs, module: GameModule): SimState {
  const level = state.level;

  // Partie gagnée : la simulation est gelée (l'écran de victoire est affiché).
  if (state.status === 'won') {
    return { ...state, inputs };
  }

  const nextTick = state.tick + 1;

  // Mort en cours : le niveau est figé le temps du décompte, puis réinitialisé.
  if (state.respawnTicks > 0) {
    const remaining = state.respawnTicks - 1;
    if (remaining > 0) {
      return { ...state, tick: nextTick, inputs, respawnTicks: remaining };
    }
    return {
      ...state,
      tick: nextTick,
      inputs,
      respawnTicks: 0,
      players: respawnedPlayers(level, module),
      entities: createEntities(level, module),
    };
  }

  const players: [SimPlayer, SimPlayer] = [
    clonePlayer(state.players[0]),
    clonePlayer(state.players[1]),
  ];
  const entities = state.entities.map(cloneEntity);

  // 1. Entrées → intention de déplacement.
  for (const index of PLAYER_INDICES) {
    const player = players[index];
    const input = inputs[index];
    // Intention horizontale dans [-1, 1] : analogique (joystick tactile) ou
    // booléens à pleine vitesse (clavier, solveur) — voir `moveAxis`.
    const direction = moveAxis(input);
    player.body.vx = direction * MOVE_SPEED_TILES_PER_S;
    if (direction !== 0) {
      player.facing = direction > 0 ? 1 : -1;
    }
    // Saut sur front montant uniquement : maintenir la touche ne relance pas.
    if (input.jump && !player.jumpHeld && player.body.onGround) {
      player.body.vy = -JUMP_SPEED_TILES_PER_S;
      player.body.onGround = false;
    }
    player.jumpHeld = input.jump;
    // Par défaut la gravité s'applique : une ability peut la couper (accroche).
    player.gravityScale = 1;
  }

  // 2. Abilities du module (jauge d'énergie, accroche, polarité…).
  for (const ability of module.abilities) {
    for (const index of PLAYER_INDICES) {
      const ctx: AbilityCtx = {
        tick: nextTick,
        dt: TICK_DT,
        level,
        player: players[index],
        partner: players[index === 0 ? 1 : 0],
        inputs: inputs[index],
        active: inputs[index][ability.input],
        entities,
      };
      ability.update(ctx);
    }
  }

  // 3. Règles d'interaction (forces magnétiques inter-joueurs et sur objets).
  const interactionCtx: InteractionCtx = {
    tick: nextTick,
    dt: TICK_DT,
    level,
    players,
    inputs,
    entities,
  };
  for (const rule of module.interactions) {
    rule.update(interactionCtx);
  }

  // 4/5. Intégration à pas fixe puis résolution des collisions, axe par axe.
  //      Les entités solides du tick précédent servent d'obstacles.
  const obstacles = solidObstacles(entities);
  const supports: (string | null)[] = [null, null];
  for (const index of PLAYER_INDICES) {
    const player = players[index];
    integrateBody(player.body, level, {
      dt: TICK_DT,
      gravity: GRAVITY_TILES_PER_S2 * player.gravityScale,
      obstacles,
    });
    supports[index] = findSupport(player.body, obstacles, CARRY_CONTACT_EPSILON)?.id ?? null;
  }

  // 6. Entités, par phase : capteurs (boutons, leviers) puis actionneurs
  //    (portes, ascenseurs) — un bouton pressé ouvre sa porte dans le tick.
  const byId = new Map<string, EntityInstance>(entities.map((e) => [e.id, e]));
  const entityCtx: EntityCtx = {
    tick: nextTick,
    dt: TICK_DT,
    level,
    players,
    entities,
    signal: (id: string) => byId.get(id)?.flags.active === 1,
    entity: (id: string) => byId.get(id),
  };
  const previousPositions = entities.map((entity) => ({ x: entity.x, y: entity.y }));
  const phases = [...new Set(entities.map((entity) => entity.phase))].sort((a, b) => a - b);
  for (const phase of phases) {
    for (const entity of entities) {
      if (entity.phase === phase && entity.update) {
        entity.update(entity, entityCtx);
      }
    }
  }

  // 7. Transport : un robot posé sur une entité mobile suit son déplacement.
  const movedObstacles = solidObstacles(entities);
  for (const index of PLAYER_INDICES) {
    const supportId = supports[index];
    if (supportId === null) {
      continue;
    }
    const entityIndex = entities.findIndex((entity) => entity.id === supportId);
    const entity = entities[entityIndex];
    const before = previousPositions[entityIndex];
    if (!entity || !before || !entity.solid) {
      continue;
    }
    const dx = entity.x - before.x;
    const dy = entity.y - before.y;
    if (dx === 0 && dy === 0) {
      continue;
    }
    const body = players[index].body;
    moveBody(
      body,
      level,
      dx,
      dy,
      movedObstacles.filter((obstacle) => obstacle.id !== supportId),
    );
    if (dy > 0) {
      // La plateforme descend : le robot reste collé au lieu de flotter un tick.
      body.y = entity.y - body.h;
    }
    body.onGround = true;
  }

  // 8. Dangers et mort.
  let died = false;
  for (const index of PLAYER_INDICES) {
    const player = players[index];
    const fellOut = player.body.y > level.height;
    if (fellOut || touchesLethalTile(player.body, level, player.character.lethalTiles)) {
      player.dead = true;
      died = true;
    }
  }
  if (died) {
    return {
      ...state,
      tick: nextTick,
      inputs,
      players,
      entities,
      deaths: state.deaths + 1,
      respawnTicks: RESPAWN_DELAY_TICKS,
    };
  }

  // 9. Conditions de victoire : chaque robot sur SA porte, au même tick.
  let allAtExit = true;
  for (const index of PLAYER_INDICES) {
    const player = players[index];
    const exit = level.exits[index];
    const center = aabbCenter(player.body);
    player.atExit = aabbContainsPoint(tileAabb(exit.x, exit.y), center);
    allAtExit &&= player.atExit;
  }

  const next: SimState = {
    ...state,
    tick: nextTick,
    inputs,
    players,
    entities,
    status: allAtExit ? 'won' : 'playing',
    wonTick: allAtExit ? nextTick : null,
  };
  return allAtExit ? { ...next, stars: computeStars(next) } : next;
}

/** Enchaîne `n` ticks avec les mêmes entrées. */
export function tickMany(
  state: SimState,
  inputs: DuoInputs,
  module: GameModule,
  ticks: number,
): SimState {
  let next = state;
  for (let i = 0; i < ticks; i += 1) {
    next = tick(next, inputs, module);
  }
  return next;
}

function playerSnapshot(player: SimPlayer): PlayerSnapshot {
  const { body } = player;
  return {
    index: player.index,
    characterId: player.character.id,
    x: body.x,
    y: body.y,
    cx: body.x + body.w / 2,
    cy: body.y + body.h / 2,
    w: body.w,
    h: body.h,
    vx: body.vx,
    vy: body.vy,
    onGround: body.onGround,
    dead: player.dead,
    atExit: player.atExit,
    facing: player.facing,
    energy: player.energy,
    fieldActive: player.fieldActive,
    flags: { ...player.flags },
  };
}

/** Projette l'état interne vers le contrat sérialisable des tests (spec §9). */
export function snapshot(state: SimState): SimStateSnapshot {
  const entities: EntitySnapshot[] = state.entities.map(entitySnapshot);
  return {
    tick: state.tick,
    seed: state.seed,
    gameId: state.gameId,
    levelId: state.level.meta.id,
    levelName: state.level.meta.name,
    status: state.status,
    won: state.status === 'won',
    dead: state.players[0].dead || state.players[1].dead,
    deaths: state.deaths,
    respawnTicks: state.respawnTicks,
    elapsedMs: (state.tick * 1000) / TICK_RATE,
    players: [playerSnapshot(state.players[0]), playerSnapshot(state.players[1])],
    inputs: [state.inputs[0], state.inputs[1]],
    grid: { width: state.level.width, height: state.level.height, tileSize: state.level.tileSize },
    entities,
    bolts: countBolts(state.entities),
    stars: state.stars,
    parTimeSeconds: state.level.meta.parTimeSeconds,
  };
}
